import { apiFetch } from "@/lib/api";

export type EffectivePlan = "trial" | "starter" | "pro" | "past_due" | "expired";

export interface SubscriptionView {
  plan: string;
  status: string;
  current_period_end?: string | null;
  cancel_at_period_end: boolean;
  trial_end?: string | null;
  provider: string;
}

export interface InvoiceRow {
  id: string;
  amount_due: number;
  currency: string;
  status: string;
  created_at: string;
  period_end?: string | null;
}

export interface BillingSnapshot {
  provider: string;
  effective_plan: EffectivePlan;
  stored_plan: string;
  subscription: SubscriptionView;
  invoices: InvoiceRow[];
  card_brand?: string | null;
  card_last4?: string | null;
  prices: Record<string, { amount: number; currency: string; interval: string; label: string }>;
}

export const billingApi = {
  snapshot: () => apiFetch<BillingSnapshot>("/billing/subscription"),
  checkout: (plan: "starter" | "pro") =>
    apiFetch<{ session_id: string; url: string }>("/billing/checkout", {
      method: "POST", body: JSON.stringify({ plan }),
    }),
  portal: () =>
    apiFetch<{ url: string }>("/billing/portal", { method: "POST" }),
  cancel: () =>
    apiFetch<SubscriptionView>("/billing/cancel", { method: "POST" }),
  resume: () =>
    apiFetch<SubscriptionView>("/billing/resume", { method: "POST" }),
  mockComplete: (session_id: string) =>
    apiFetch<any>("/billing/mock/complete", {
      method: "POST", body: JSON.stringify({ session_id }),
    }),
  // Preview-only — backdates trial_ends_at for demo purposes.
  devExpireTrial: () =>
    apiFetch<{ ok: true }>("/billing/_dev/expire-trial", { method: "POST" }),
};

export const PLAN_FEATURES_FRONT: Record<EffectivePlan, Set<string>> = {
  trial:    new Set(["scan", "outfits", "loans", "reports", "team", "audit_log", "csv_import", "share_links_unlimited", "core_catalog"]),
  pro:      new Set(["scan", "outfits", "loans", "reports", "team", "audit_log", "csv_import", "share_links_unlimited", "core_catalog"]),
  starter:  new Set(["core_catalog", "share_links_basic"]),
  past_due: new Set(["core_catalog"]),
  expired:  new Set(["core_catalog"]),
};

export function planLabel(plan: EffectivePlan): string {
  switch (plan) {
    case "trial": return "Free trial";
    case "starter": return "Starter";
    case "pro": return "Pro";
    case "past_due": return "Past due";
    case "expired": return "Expired";
  }
}

export function isReadOnly(plan: EffectivePlan): boolean {
  return plan === "expired" || plan === "past_due";
}
